import React, { useMemo, useCallback, memo } from 'react';
import { useMutation } from 'react-query';
import classNames from 'classnames';
import { IconEllipsis } from '@ace/react-icons';
import { useOverlayToggle, fetchHelper } from '@ace/react-utilities';
import AceAnswersEventPartials from 'Components/Card/Partials/Partials.AceAnswersEvent';
import EventPartials from 'Components/Card/Partials/Partials.Event';
import useHasEventTimePassed from 'Components/Card/helper/eventTimingHelpers';
import useCard from 'context/CardContext';
import buttonStyles from 'assets/css/Button.module.css';
import overlayStyles from 'assets/css/ToolTipOverlay.module.css';
import eventStyles from 'assets/css/Event.module.css';
import * as Sentry from "@sentry/react";
import { getUTCDateFromServerDate } from 'utils/dateUtils';

export default function AceAnswersRegisteredEvent(props) {
    return (
        <AceAnswersEventPartials.EventDisplay {...props} >
            <CancelMenu {...props} />
            <JoinBtn {...props} />
        </AceAnswersEventPartials.EventDisplay>
    );
}

function CancelMenu(props) {
    const { event } = props;

    //hooks
    const { showOverlay, toggleOverlay, overlayRef } = useOverlayToggle();

    //local var(s)
    const joinDate = useMemo(() => (getUTCDateFromServerDate(event.EventJoinTime)), [event.EventJoinTime]);

    const canCancel = !useHasEventTimePassed(joinDate);

    const MenuIcon = useMemo(
        () => (<i className={`${eventStyles["icon-ellipsis"]}`} onClick={() => toggleOverlay()}><IconEllipsis fill="#b5b9bc" /></i>), 
        [toggleOverlay]
    );

    const OverlayContent = useMemo(() => {
        const overlayClasses = classNames(overlayStyles.overlayWrap, overlayStyles.overlayCancelEvent, overlayStyles.showOverlay);

        return (
            <div ref={overlayRef} className={overlayClasses}>
                <AceAnswersRegisteredEventCancelBtn cancelUrl={event.EventCancelUrl} />
            </div>
        );
    }, [event.EventCancelUrl, overlayRef]);

    if (canCancel) {
        return (
            <>
                {MenuIcon}
                {showOverlay && OverlayContent}
            </>
        );
    }

    return false;
}

const CancelStateBtn = ({text}) => (<span className={buttonStyles.stroke} style={{padding: ".5rem"}}>{text}</span>);
const CanceledBtn = memo(() => (<CancelStateBtn text="Canceled" />));
const CancelingBtn = memo(() => (<CancelStateBtn text="Canceling..." />));
const CancelErrorBtn = memo(() => (<CancelStateBtn text="Error" />));

function AceAnswersRegisteredEventCancelBtn({cancelUrl}) {
    //context(s)
    const { refreshCard } = useCard();

    //event(s)
    const { 
        mutate: cancelEvent, 
        reset: cancelReset, 
        isSuccess: isCanceled, 
        isLoading: isCanceling,
        isError: isCancelError
    }  = useMutation(callbackUrl => fetchHelper(callbackUrl, { method: 'DELETE' }), {
        onSuccess: () => {
            setTimeout(() => {
                refreshCard();
                cancelReset();
            }, 5000);
        },
        onError: (error) => {
            Sentry.captureException(error);
            setTimeout(() => {
                refreshCard();
                cancelReset();
            }, 2000);
        }
    });

    const handleCancelClick = useCallback(() => {
        cancelEvent(cancelUrl);
    }, [cancelUrl, cancelEvent]);

    const CancelBtn = () => (
        <button className={`${buttonStyles.stroke} ${eventStyles.cancelEventBtn}`} onClick={() => handleCancelClick()}>
            Cancel
            <EventPartials.WrappedIconAngleRight />
        </button>
    );

    if (isCanceled) {
        return <CanceledBtn />
    } else if (isCanceling) {
        return <CancelingBtn />
    } else if (isCancelError) {
        return <CancelErrorBtn />
    } else {
        return <CancelBtn />;
    }
}

function JoinBtn(props) {
    //local var(s)
    const { event } = props;

    const displayProps = {
        startDate: getUTCDateFromServerDate(event.EventJoinTime), 
        endDate: getUTCDateFromServerDate(event.EventEnd), 
        beforeEventText: "Scheduled",
        afterEventText: "Finished",
        duringEventText: "Join Webinar"
    };

    const hasStarted = useHasEventTimePassed(displayProps.startDate);
    const hasEnded = useHasEventTimePassed(displayProps.endDate);

    if (hasStarted && !hasEnded) {
        return (
            <EventPartials.EventLaunchBtn href={event.EventJoinUrl} additionalClassNames={"eligible-event"}>
                <EventPartials.EventLaunchBtnContent {...displayProps} />
            </EventPartials.EventLaunchBtn>
        );
    }

    return (
        <span className={`${buttonStyles.stroke} ${buttonStyles.strokeDisabled}`}>
            <EventPartials.EventLaunchBtnContent {...displayProps} />
        </span>
    );
}